import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  NgZone,
} from '@angular/core';

import { ComponentsService } from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { UserComponentsService } from '../../template-components/services/user-components.service';
import { BrandingService } from '../../template-components/services/branding.service';

type EditorSidebarItemId =
  | 'layers'
  | 'brand'
  | 'text'
  | 'images'
  | 'components';

type EditorSidebarItem = {
  id: EditorSidebarItemId;
  label: string;
  ariaLabel: string;
  icon: string;
  activeIcon?: string;
  activeColor: string;
};

type TextPreset = {
  id: 'heading' | 'subheading' | 'body';
  label: string;
  value: string;
  fontsize: number;
  formatting?: string;
};

@Component({
  selector: 'template-attribute-editor',
  templateUrl: './template-attribute-editor.component.html',
  styleUrls: ['./template-attribute-editor.component.scss'],
  standalone: false,
})
export class TemplateAttributeEditorComponent implements OnDestroy {
  private handleMessageBind;
  private activeSidebarItem: EditorSidebarItemId | null = 'layers';
  private isMobileSidebarLayout = false;
  public componentsFactory = inject(ComponentsService);
  private ngZone = inject(NgZone);
  public attributeDataService = inject(AttributeDataService);
  public userComponentsService = inject(UserComponentsService);
  private brandingService = inject(BrandingService);
  private templateEditorService = inject(TemplateEditorService);

  sidebarItems: EditorSidebarItem[] = [
    {
      id: 'layers',
      label: 'Layers',
      ariaLabel: 'Show layers',
      icon: 'editor-layers',
      activeIcon: 'editor-layers-filled',
      activeColor: '#2563eb',
    },
    {
      id: 'brand',
      label: 'Brand',
      ariaLabel: 'Edit brand settings',
      icon: 'branding-palette',
      activeColor: '#eab308',
    },
    {
      id: 'text',
      label: 'Text',
      ariaLabel: 'Add text',
      icon: 'editor-text',
      activeColor: '#00a6f4',
    },
    {
      id: 'images',
      label: 'Images',
      ariaLabel: 'Add image',
      icon: 'image',
      activeColor: '#22c55e',
    },
    {
      id: 'components',
      label: 'Components',
      ariaLabel: 'Add component',
      icon: 'editor-components',
      activeColor: '#a855f7',
    },
  ];

  textPresets: TextPreset[] = [
    {
      id: 'heading',
      label: 'Add a heading',
      value: 'Add a heading',
      fontsize: 72,
      formatting: 'bold',
    },
    {
      id: 'subheading',
      label: 'Add a subheading',
      value: 'Add a subheading',
      fontsize: 44,
    },
    {
      id: 'body',
      label: 'Add some body text',
      value: 'Add some body text',
      fontsize: 28,
    },
  ];

  constructor() {
    this.componentsFactory.reset();
    this.syncSidebarLayout();

    this.handleMessageBind = this._handleMessageFromTemplate.bind(this);

    window.addEventListener('message', this.handleMessageBind);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleMessageBind);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncSidebarLayout();
  }

  getActiveSidebarItem(): EditorSidebarItemId | null {
    if (this.componentsFactory.showAttributeList) {
      return 'layers';
    }

    const selected = this.componentsFactory.selected;
    const selectedType = selected?.type || selected?.tagName;

    if (selectedType === 'rise-playlist-item-selector') {
      return 'components';
    }

    if (selectedType === 'rise-branding') {
      return 'brand';
    }

    if (selectedType === 'rise-text') {
      return 'text';
    }

    if (
      selectedType === 'rise-folder' ||
      selectedType === 'rise-stock-library'
    ) {
      return this.getSelectedFolderType(selected) === 'canva'
        ? 'components'
        : 'images';
    }

    return this.activeSidebarItem;
  }

  private getSelectedFolderType(selected: {
    id?: string;
    attributes?: { type?: string };
    options?: { fileType?: string };
  }): string | null {
    if (!selected) {
      return null;
    }

    if (selected.id) {
      const attributeType = this.attributeDataService.getAvailableAttributeData(
        selected.id,
        'type',
      );
      if (attributeType != null) {
        return attributeType;
      }
    }

    if (selected.attributes?.type) {
      return selected.attributes.type;
    }

    if (selected.options?.fileType) {
      return selected.options.fileType;
    }

    return null;
  }

  selectSidebarItem(itemId: EditorSidebarItemId): void {
    this.activeSidebarItem = itemId;

    switch (itemId) {
      case 'layers':
        this.componentsFactory.backToList();
        break;
      case 'brand':
        this.editBrandSettings();
        break;
      case 'text':
        this.showTextAddPanel();
        break;
      case 'images':
        this.userComponentsService.addItem('rise-folder', {
          fileType: 'image',
          singleFile: true,
        });
        break;
      case 'components':
        this.componentsFactory.editComponent(
          {
            type: 'rise-playlist-item-selector',
          },
          {
            userComponents: true,
          },
        );
        break;
    }
  }

  isTextAddPanelVisible(): boolean {
    return (
      !this.componentsFactory.showAttributeList &&
      !this.componentsFactory.selected &&
      this.activeSidebarItem === 'text'
    );
  }

  isComponentSettingsVisible(): boolean {
    return (
      !this.componentsFactory.showAttributeList &&
      !!this.componentsFactory.selected &&
      !this.isTextAddPanelVisible()
    );
  }

  isSidebarPanelVisible(): boolean {
    return (
      this.componentsFactory.showAttributeList ||
      this.isTextAddPanelVisible() ||
      this.isComponentSettingsVisible()
    );
  }

  closeSidebarPanel(): void {
    if (!this.isMobileViewport()) {
      this.componentsFactory.backToList();
      return;
    }

    if (this.componentsFactory.selected) {
      this.componentsFactory.backToList();
    }

    this.activeSidebarItem = null;
    this.componentsFactory._showAttributeList(false);
  }

  addTextBox(): void {
    this.userComponentsService.addItem('rise-text');
  }

  addTextPreset(preset: TextPreset): void {
    this.userComponentsService.addItem('rise-text', {
      attributes: {
        value: preset.value,
        fontsize: preset.fontsize,
        formatting: preset.formatting || '',
      },
    });
  }

  private showTextAddPanel(): void {
    if (this.componentsFactory.selected) {
      this.componentsFactory.backToList();
    }

    this.componentsFactory._showAttributeList(false);
  }

  private syncSidebarLayout(): void {
    const isMobileViewport = this.isMobileViewport();

    if (isMobileViewport === this.isMobileSidebarLayout) {
      return;
    }

    this.isMobileSidebarLayout = isMobileViewport;

    if (!isMobileViewport) {
      if (!this.componentsFactory.selected && this.activeSidebarItem === null) {
        this.activeSidebarItem = 'layers';
        this.componentsFactory._showAttributeList(true);
      }

      return;
    }

    if (!this.componentsFactory.selected) {
      this.activeSidebarItem = null;
      this.componentsFactory._showAttributeList(false);
    }
  }

  private isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    );
  }

  private editBrandSettings(): void {
    const brandingComponent = this.brandingService.getBrandingComponent() || {
      type: 'rise-branding',
    };

    this.componentsFactory.editComponent(brandingComponent);
  }

  _handleMessageFromTemplate(event) {
    var data = event.data;

    if ('string' === typeof event.data) {
      try {
        data = JSON.parse(event.data);
      } catch (e) {}
    }

    switch (data.type) {
      case 'deleteComponent':
        this.userComponentsService.removeUserComponent(data.value.id);
        this.componentsFactory.editHighlightedComponent(null);
        this.ngZone.run(() => {});
        break;
      case 'editComponent':
        this.componentsFactory.editHighlightedComponent(data.value);
        this.ngZone.run(() => {});
        break;
      case 'saveComponentPosition':
        const customization =
          this.attributeDataService.getAttributeData(
            data.value.id,
            'customization',
          ) || {};
        const updatedCustomization = {
          ...customization,
          ...data.value.customization,
        };
        this.attributeDataService.setAttributeData(
          data.value.id,
          'customization',
          updatedCustomization,
        );
        this.ngZone.run(() => {});
        break;
      case 'highlightedComponent':
        this.componentsFactory.showContextualToolbar(data.value);
        this.ngZone.run(() => {});
        break;
      case 'rise-text-focus-in':
        this.ngZone.run(() => {
          this.templateEditorService.isInlineEditing = true;
        });
        break;
      case 'rise-text-focus-out':
        this.ngZone.run(() => {
          this.templateEditorService.isInlineEditing = false;
        });
        break;
      case 'undoShortcut':
        this.ngZone.run(() => {
          this.templateEditorService.undo();
        });
        break;
      case 'redoShortcut':
        this.ngZone.run(() => {
          this.templateEditorService.redo();
        });
        break;
      default:
    }
  }
}
